import { Facility, GetAllNotificationsResponse } from '@fullstory/proto/fs/services/pinguin';
import { combineReducers, RequestActionType } from '@fullstory/redux-util';
import { NotificationsRequestActions } from './actions';
import { NotificationsRequestTypes } from './types';

interface GetAllNotificationsResponseWithError extends GetAllNotificationsResponse {
  error: boolean;
}
const initialState: GetAllNotificationsResponseWithError = {
  notifications: [],
  cursor: '',
  resultCount: 0,
  unreadCount: 0,
  error: false,
};

const allNotifications = (
  state: GetAllNotificationsResponseWithError = initialState,
  action: RequestActionType<typeof NotificationsRequestActions>,
): GetAllNotificationsResponseWithError => {
  switch (action.type) {
    case NotificationsRequestTypes.GetAllNotificationsRequestSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const cursor = body.cursor;

      if (!cursor) {
        return {
          ...action.data,
          error: false,
        };
      } else {
        // if we have a cursor, we want to append the result to the existing notification list rather than override it
        return {
          ...state,
          notifications: [...state.notifications, ...action.data.notifications],
          cursor: action.data.cursor,
          resultCount: action.data.resultCount,
          unreadCount: action.data.unreadCount,
          error: false,
        };
      }
    }
    case NotificationsRequestTypes.GetAllNotificationsRequestFailure: {
      // if we have a permissions error, persist to redux so we can stop polling
      if (action.error.status === 403) {
        return {
          ...state,
          error: true,
        };
      }
      return state;
    }
    case NotificationsRequestTypes.MarkAllNotificationsRequestSuccess: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      const facilities = body.facilities;
      // read will always be set -- the api will error if it isn't
      const read = body.read ?? false;
      const notifications = state.notifications;

      if (!facilities.length) {
        // if no facility specified, everything was marked as (un)read
        notifications.forEach(n => (n.read = read));
      } else {
        notifications.forEach(n => {
          if (facilities.includes(n.configId?.facility ?? Facility.FACILITY_INVALID)) {
            n.read = read;
          }
        });
      }

      return {
        ...state,
        notifications: [...notifications],
      };
    }
    default:
      return state;
  }
};

type NotificationsReducerState = {
  allNotifications: ReturnType<typeof allNotifications>;
};

export const notificationsReducer = combineReducers<NotificationsReducerState>({
  allNotifications,
});
